/**
 * ============================================================================
 * ADMIN STORE - ZUSTAND
 * ============================================================================
 * 
 * Global admin state management
 */

import { create } from 'zustand';

const useAdminStore = create((set, get) => ({
  // State
  dashboardMetrics: null,
  analytics: null,
  transactions: [],
  disputes: [],
  users: [],
  withdrawals: [],
  settings: null,
  auditLogs: [],
  systemHealth: null,
  loading: false,
  error: null,
  filters: {
    dateRange: null,
    status: null,
    type: null,
  },
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
  },

  // Actions
  setDashboardMetrics: (metrics) => {
    set({ dashboardMetrics: metrics });
  },

  setAnalytics: (analytics) => {
    set({ analytics });
  },

  setTransactions: (transactions) => {
    set({ transactions });
  },

  updateTransaction: (transactionId, updates) => {
    set((state) => ({
      transactions: state.transactions.map((t) =>
        t.id === transactionId ? { ...t, ...updates } : t
      ),
    }));
  },

  setDisputes: (disputes) => {
    set({ disputes });
  },

  updateDispute: (disputeId, updates) => {
    set((state) => ({
      disputes: state.disputes.map((d) =>
        d.id === disputeId ? { ...d, ...updates } : d
      ),
    }));
  },

  setUsers: (users) => {
    set({ users });
  },

  updateUser: (userId, updates) => {
    set((state) => ({
      users: state.users.map((u) =>
        u.id === userId ? { ...u, ...updates } : u
      ),
    }));
  },

  setWithdrawals: (withdrawals) => {
    set({ withdrawals });
  },

  updateWithdrawal: (withdrawalId, updates) => {
    set((state) => ({
      withdrawals: state.withdrawals.map((w) =>
        w.id === withdrawalId ? { ...w, ...updates } : w
      ),
    }));
  },

  setSettings: (settings) => {
    set({ settings });
  },

  updateSetting: (key, value) => {
    set((state) => ({
      settings: state.settings
        ? { ...state.settings, [key]: value }
        : { [key]: value },
    }));
  },

  setAuditLogs: (logs) => {
    set({ auditLogs: logs });
  },

  addAuditLog: (log) => {
    set((state) => ({
      auditLogs: [log, ...state.auditLogs],
    }));
  },

  setSystemHealth: (health) => {
    set({ systemHealth: health });
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 },
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        dateRange: null,
        status: null,
        type: null,
      },
      pagination: { ...get().pagination, page: 1 },
    });
  },

  setPagination: (pagination) => {
    set((state) => ({
      pagination: { ...state.pagination, ...pagination },
    }));
  },

  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    set({ error });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      dashboardMetrics: null,
      analytics: null,
      transactions: [],
      disputes: [],
      users: [],
      withdrawals: [],
      settings: null,
      auditLogs: [],
      systemHealth: null,
      loading: false,
      error: null,
      filters: {
        dateRange: null,
        status: null,
        type: null,
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
      },
    });
  },
}));

export default useAdminStore;
